import { useAuthStore } from '@stores/authStore';
import { useCallAudioStore } from '@stores/call/callAudioStore';
import { CallEventType, useCallEventStore } from '@stores/call/callEventStore';
import { useCallReconnectStore } from '@stores/call/callReconnectStore';
import { useCallStore } from '@stores/call/callStore';
import {
  type CallMetaData,
  P2pDataType,
  type P2pMessage,
  P2pSystemMsg,
} from '@stores/call/callTypes';
import { useLocalMediaStore } from '@stores/call/localMediaStore';
import { useMediaConnectionsStore } from '@stores/call/mediaConnectionsStore';
import Peer, { type DataConnection } from 'peerjs';
import { defineStore } from 'pinia';
import { Subject } from 'rxjs';
import { ref, type Ref, shallowRef, type ShallowRef } from 'vue';

type DataConnectionsStore = {
  storeNewDataConnection: (dataConnection: DataConnection) => boolean;
  sendP2pData: (userId: string, msg: P2pMessage) => Promise<void>;
  dataConnections: Ref<Map<string, any>>;
  remoteCallMetadata: Ref<Map<string, CallMetaData>>;
  broadcastP2pData: (msg: P2pMessage) => void;
  dataConnectionTime: Ref<Date | undefined>;
  sendRemoteAudioOff: (userId: string) => void;
  sendRemoteVideoOff: (userId: string) => void;
  incommingMsg: ShallowRef<Subject<{ msg: string; name: string }>>;
  focusedCallMetadata: Ref<CallMetaData | null>;
  addDataConnectionListeners: (dataConnection: DataConnection) => void;
  createNewDataConnection: (peerJs: Peer, userId?: string) => Promise<boolean>;
};

export const useDataConnectionsStore = defineStore('dataConnections', (): DataConnectionsStore => {
  const authStore = useAuthStore();
  const callStore = useCallStore();
  const localMediaStore = useLocalMediaStore();
  const callEventStore = useCallEventStore();
  const callAudioStore = useCallAudioStore();
  const mediaConnectionsStore = useMediaConnectionsStore();
  const callReconnectStore = useCallReconnectStore();

  const dataConnections = ref<Map<string, any>>(new Map<string, any>());
  const remoteCallMetadata = ref<Map<string, CallMetaData>>(new Map<string, CallMetaData>());
  const focusedCallMetadata = ref<CallMetaData | null>(null);
  const dataConnectionTime = ref<Date | undefined>(undefined);
  const incommingMsg = shallowRef<Subject<{ msg: string; name: string }>>(
    new Subject<{ msg: string; name: string }>(),
  );

  const addDataConnectionListeners = (dataConnection: DataConnection) => {
    dataConnection.removeAllListeners();
    /**
     * data-message from peer
     */
    dataConnection.on('data', async (data: unknown) => {
      callEventStore.emit(CallEventType.P2P_DATA_CONNECTION, 'data', { data });
      const msg: P2pMessage = JSON.parse(data as any);
      await handleP2pMsg(msg);
    });

    /**
     * connection to peer is closed
     *
     */
    dataConnection.on('close', () => {
      callEventStore.emit(CallEventType.P2P_DATA_CONNECTION, 'close');
      callStore.disconnectPeer(dataConnection.peer);
    });

    /**
     * error in dataConnection to peer
     */
    dataConnection.on('error', (error) => {
      callEventStore.emit(CallEventType.P2P_DATA_CONNECTION, 'error', { error });

      switch (error.type) {
        case 'not-open-yet':
          break;
        case 'message-too-big':
          break;
        case 'negotiation-failed':
          callStore.disconnectPeer(dataConnection.peer);
          callReconnectStore.startReconnectingToPeer(dataConnection.peer);
          break;
        case 'connection-closed':
          callStore.disconnectPeer(dataConnection.peer);
          callReconnectStore.startReconnectingToPeer(dataConnection.peer);
          break;
        default:
          break;
      }
    });

    /**
     * ice-state in dataConnection changed
     */
    dataConnection.on('iceStateChanged', (event: RTCIceConnectionState) => {
      callEventStore.emit(CallEventType.P2P_DATA_CONNECTION, 'iceStateChanged', event);
      if (event === 'failed') {
        callStore.disconnectPeer(dataConnection.peer);
        callReconnectStore.startReconnectingToPeer(dataConnection.peer);
      }
    });

    /**
     * connection to new peer is open
     */
    dataConnection.on('open', async () => {
      callEventStore.emit(CallEventType.P2P_DATA_CONNECTION, 'open', {});
      callAudioStore.playAudio(callAudioStore.peerConnectAudio);

      const localPeerId = authStore.authContext?.user?.id;
      if (!localPeerId) {
        await callStore.stopCallOnHardError('connection');
        return;
      }

      // send own media-state to new peer
      const videoMsg = localMediaStore.localVideoMuted
        ? P2pSystemMsg.ownVideoOff
        : P2pSystemMsg.ownVideoOn;
      const audioMsg = localMediaStore.localAudioMuted
        ? P2pSystemMsg.ownAudioOff
        : P2pSystemMsg.ownAudioOn;
      await sendP2pData(dataConnection.peer, {
        userId: localPeerId,
        content: audioMsg,
        type: P2pDataType.system,
      });
      await sendP2pData(dataConnection.peer, {
        userId: localPeerId,
        content: videoMsg,
        type: P2pDataType.system,
      });
    });
  };

  /**
   *  stores info for data-connection in state
   */
  const storeNewDataConnection = (dataConnection: DataConnection) => {
    // don't run twice
    if (dataConnections.value.has(dataConnection.peer)) {
      return false;
    }
    dataConnections.value.set(dataConnection.peer, dataConnection);
    remoteCallMetadata.value.set(dataConnection.peer, {
      audioEnabled: true,
      videoEnabled: true,
    });
    return true;
  };

  /**
   *  - create a new data-connection
   */
  const createNewDataConnection = (peerJs: Peer, userId?: string) => {
    return Promise.race<boolean>([
      new Promise((resolve) => {
        if (!userId) {
          resolve(false);
          return;
        }
        // don't run twice
        if (dataConnections.value.has(userId)) {
          resolve(false);
          return;
        }
        const dataConnection: DataConnection = peerJs.connect(userId);
        storeNewDataConnection(dataConnection);
        addDataConnectionListeners(dataConnection);
        resolve(true);
      }),
      new Promise((resolve) => {
        setTimeout(() => {
          resolve(false);
        }, 10000);
      }),
    ]);
  };

  /** sends a p2p-data-message one connected peer */
  const sendP2pData = async (userId: string, msg: P2pMessage) => {
    const dataConnection = dataConnections.value.get(userId);
    const localPeerId = authStore.authContext?.user?.id;

    if (!dataConnection) {
      return;
    }

    if (!localPeerId) {
      callStore.stopCallOnHardError('connection');
      return;
    }

    msg.userId = localPeerId;
    await dataConnection.send(JSON.stringify(msg));
  };

  /** handles various p2p-messages */
  const handleP2pMsg = async (msg: P2pMessage) => {
    if (msg.type === P2pDataType.chat) {
      handleChatMsg({ msg: msg.content || '', name: msg.name || '' });
      return;
    }

    if (msg.type === P2pDataType.system) {
      const msgPeerCallMetadata = getRemoteCallMetadata(msg.userId);
      switch (msg.content) {
        // beeing forced to turn off my own video
        case P2pSystemMsg.videoOff:
          localMediaStore.setLocalVideoMutedState(true);
          break;

        // beeing forced to turn off my own audio
        case P2pSystemMsg.audioOff:
          localMediaStore.setLocalAudioMutedState(true);
          break;

        // setting remote peer's audio state to enabled
        case P2pSystemMsg.ownAudioOn:
          msgPeerCallMetadata.audioEnabled = true;
          remoteCallMetadata.value.set(msg.userId, msgPeerCallMetadata);
          break;

        // setting remote peer's audio state to disabled
        case P2pSystemMsg.ownAudioOff:
          msgPeerCallMetadata.audioEnabled = false;
          remoteCallMetadata.value.set(msg.userId, msgPeerCallMetadata);
          break;

        // setting remote peer's video state to enabled
        case P2pSystemMsg.ownVideoOn:
          msgPeerCallMetadata.videoEnabled = true;
          remoteCallMetadata.value.set(msg.userId, msgPeerCallMetadata);
          break;

        // setting remote peer's video state to disabled
        case P2pSystemMsg.ownVideoOff:
          msgPeerCallMetadata.videoEnabled = false;
          remoteCallMetadata.value.set(msg.userId, msgPeerCallMetadata);
          break;

        // focus on a specific user
        case P2pSystemMsg.focus:
          const newStream = mediaConnectionsStore.remoteStreamsMap.get(msg.userId);
          if (newStream) {
            mediaConnectionsStore.lockedFocusUserId = msg.userId;
            mediaConnectionsStore.updateFocusedStream({
              stream: newStream,
              userId: msg.userId,
              init: true,
            });
          }
          break;

        // stop focus-mode
        case P2pSystemMsg.focusEnd:
          mediaConnectionsStore.lockedFocusUserId = undefined;
          break;
      }
      return;
    }
  };

  const handleChatMsg = (msg: { msg: string; name: string }) => {
    msg.msg = decodeURIComponent(window.atob(msg.msg));
    incommingMsg.value.next(msg);
  };

  /** sends a p2p-data-message to all connected peers */
  const broadcastP2pData = (msg: P2pMessage) => {
    [...dataConnections.value.keys()].forEach((peerId) => {
      sendP2pData(peerId, msg);
    });
  };

  /** messages a patient from a therapist forcing an audio-mute */
  const sendRemoteAudioOff = (userId: string) => {
    const msg: P2pMessage = {
      userId: authStore.authContext?.user?.id || '',
      type: P2pDataType.system,
      content: P2pSystemMsg.audioOff,
    };
    sendP2pData(userId, msg);
  };

  /** messages a patient from a therapist forcing an video-mute */
  const sendRemoteVideoOff = (userId: string) => {
    const msg: P2pMessage = {
      userId: authStore.authContext?.user?.id || '',
      type: P2pDataType.system,
      content: P2pSystemMsg.videoOff,
    };
    sendP2pData(userId, msg);
  };

  const getRemoteCallMetadata = (userId: string) => {
    return (
      remoteCallMetadata.value.get(userId || '') || {
        audioEnabled: false,
        videoEnabled: false,
      }
    );
  };

  const store: DataConnectionsStore = {
    storeNewDataConnection,
    sendP2pData,
    dataConnections,
    remoteCallMetadata,
    broadcastP2pData,
    dataConnectionTime,
    sendRemoteAudioOff,
    sendRemoteVideoOff,
    incommingMsg,
    focusedCallMetadata,
    addDataConnectionListeners,
    createNewDataConnection,
  };
  return store;
});
